import { useState, useEffect } from "react";
// @mui
import { useTheme } from "@mui/material/styles";
import {
  MenuItem,
  TextField,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
} from "@mui/material";
// context
import usePlannerSettings from "../../../contexts/PlannerContext";
// utils
import axios from "../../../utils/axios";
//
import { useTranslation } from "react-i18next";
import {resetGlobalVisFacesFinitureHackFor3D} from "../../../utils/globalFacesFinitureHackFor3D";

// ----------------------------------------------------------------------

export default function SettingPlannerFinishes(props) {
  const theme = useTheme();
  const { t } = useTranslation();

  const {
    line,
    setLine,
    visibleFacesVariation,
    setVisibleFacesVariation,
    hiddenFacesVariation,
    setHiddenFacesVariation,
    nonVisibleFacesAvailable,
    setNonVisibleFacesAvailable,
  } = usePlannerSettings();
  const plan = usePlannerSettings((state) => state.plan);

  const [atSightFacesFinitureArr, setAtSightFacesFinitureArr] = useState([
    { value: 0, label: "Seleziona una finitura" },
  ]);
  const [hiddenFacesFinitureArr, setHiddenFacesFinitureArr] = useState([
    { value: 0, label: "Seleziona una finitura" },
  ]);
  const [visibleFacesVariationAlert, setVisibleFacesVariationAlert] =
    useState(null);
  const [visibleMaterialName, setVisibleMaterialName] = useState("");
  const [hiddenMaterialName, setHiddenMaterialName] = useState("");

  const style = {
    "& > *": { my: "8px !important" },
  };

  const getFiniture = async () => {
    try {
      const res = await axios.post(`/planner/materials/${line}/variations`);

      let oldVisibleVariation = null;
      if (visibleFacesVariation) {
        const r = await axios.post(
          `/planner/variations/${visibleFacesVariation}`
        );
        oldVisibleVariation = r.data.variation;
      }

      var visFacesVariations = [],
        hidFacesVariations = [];
      var hiddenFacesVariationAvailabe = false;
      var currentFrontVariationId = 0;
      var currentHiddVariationId = 0;

      res.data.variations.forEach((variation) => {
        if (variation.at_sight) {
          visFacesVariations.push({
            value: variation.id,
            label: variation.variation_nicename,
          });
          if (currentFrontVariationId === 0) {
            // remember the first element in order to select it by default
            currentFrontVariationId = variation.id;
          }

          // if the current element is equals to the stored value, comparing nicenames, we have to select it
          // Case study: Quando si è nella finitura Metallo e si sceglie il colore (per esempio “white”), quando poi si selezionano le lastre in plastica, bisogna riselezionare il colore del metallo già scelto e selezionato in precedenza
          // example: Passing from Metallo to Plastica + Metallo, we have Metallo Bianco variation but we have different IDs, due to material groups, so we have to check its nicename
          if (
            oldVisibleVariation &&
            variation.variation_nicename ==
              oldVisibleVariation.variation_nicename
          ) {
            currentFrontVariationId = variation.id;
          }

          if (variation.id == visibleFacesVariation) {
            // if the current element is equals to the stored value, we have to select it
            currentFrontVariationId = variation.id;
          }
        } else {
          // TODO select the correct element (see here upper as for at sight faces)
          hiddenFacesVariationAvailabe = true;
          hidFacesVariations.push({
            value: variation.id,
            label: variation.variation_nicename,
          });
          if (currentHiddVariationId === 0) {
            // remember the first element in order to select it by default
            currentHiddVariationId = variation.id;
          }

          if (variation.id == hiddenFacesVariation) {
            // if the current element is equals to the stored value, we have to select it
            currentHiddVariationId = variation.id;
          }
        }
      });

      setVisibleFacesVariation(currentFrontVariationId); // store the new element selected (the value is used to select the option)
      setHiddenFacesVariation(currentHiddVariationId); // store the new element selected (the value is used to select the option)

      resetGlobalVisFacesFinitureHackFor3D(visFacesVariations)
      setAtSightFacesFinitureArr(visFacesVariations); // fill the dropdown
      setHiddenFacesFinitureArr(hidFacesVariations); // fill the dropdown

      // *****
      // tell everyone that no variation are available for hidden faces means that no need to select any at sight faces.. all the structure will be make with the same finiture
      if (hiddenFacesVariationAvailabe) {
        setNonVisibleFacesAvailable(true);
      } else {
        setNonVisibleFacesAvailable(false);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getMaterialNames = () => {
    switch (line) {
      case 3:
        setVisibleMaterialName("materials.stone");
        setHiddenMaterialName("materials.plastic");
        break;
      case 10:
        setVisibleMaterialName("materials.metal");
        setHiddenMaterialName("materials.plastic");
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    if (line) {
      getFiniture();
      getMaterialNames();
    }
  }, [line]);

  /**
   * Convert material from plastica or metallo to metallo + plastica
   */
  const handleConvertMaterial = () => {
    if (line === 1 || line === 2) {
      setLine(10);
    }
  };

  function handleChange(element, value) {
    if (element == "visibleFaceVariations") {
      // Check if there are some invalid variations in combo with some materials or other specs
      const gotErrors = checkInvalidVisibleVariations(value);

      if (!gotErrors) {
        setVisibleFacesVariation(value);
        // Reset alert errors
        setVisibleFacesVariationAlert(null);
      } else {
        // Retrieve error and display inside an alert
        setVisibleFacesVariationAlert(t(gotErrors));
        // Remove alert after 8 seconds
        setTimeout(() => {
          setVisibleFacesVariationAlert(null);
        }, 8000);
      }
    } else {
      setHiddenFacesVariation(value);
    }
  }

  const checkInvalidVisibleVariations = (value) => {
    if (line === 1) {
      // Material: plastic
      const visibleVariation = atSightFacesFinitureArr.find(
        (e) => e.value === value
      );

      if (!visibleVariation) {
        // Variation does not exist
        return "api.errors.not_found.variation";
      }

      if (visibleVariation.value === 4 || visibleVariation.value === 6) {
        // Variation: plastic terracotta colour / dark grey

        // Check if plan has blocks with height !== 50
        let blocks50 = true;
        for (let i = 0; i < plan.length && blocks50; i++) {
          for (let j = 0; j < plan[i].length && blocks50; j++) {
            if (plan[i][j]["height"] !== 0 && plan[i][j]["height"] !== 50) {
              blocks50 = false;
            }
          }
        }
      }
    }

    return null;
  };

  return (
    <>
      <Card sx={{ margin: "16px 0" }}>
        <CardContent title="Type" sx={style}>
          {nonVisibleFacesAvailable && (
            <Typography component="h6" variant="h6">
              {t("settings.drawer.finishes.visible")} {t(visibleMaterialName)}
            </Typography>
          )}
          <Typography>{t("settings.drawer.finishes.select")}</Typography>
          {visibleFacesVariationAlert && (
            <Alert severity="error">{visibleFacesVariationAlert}</Alert>
          )}
          <TextField
            select
            fullWidth
            name="visibleFaceVariations"
            label={t("settings.drawer.finishes.select_label")}
            onChange={(e) => handleChange(e.target.name, e.target.value)}
            value={visibleFacesVariation}
          >
            {atSightFacesFinitureArr &&
              atSightFacesFinitureArr.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(`material_variations.${option.label}`)}
                </MenuItem>
              ))}
          </TextField>
        </CardContent>
      </Card>

      {nonVisibleFacesAvailable && (
        <Card sx={{ margin: "16px 0" }}>
          <CardContent title="Type" sx={style}>
            <Typography component="h6" variant="h6">
              {t("settings.drawer.finishes.hidden")} {t(hiddenMaterialName)}
            </Typography>
            <Typography>{t("settings.drawer.finishes.select")}</Typography>
            <TextField
              select
              fullWidth
              name="hiddenFaceVariations"
              label={t("settings.drawer.finishes.select_label")}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              value={hiddenFacesVariation}
            >
              {hiddenFacesFinitureArr &&
                hiddenFacesFinitureArr.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(`material_variations.${option.label}`)}
                  </MenuItem>
                ))}
            </TextField>
          </CardContent>
        </Card>
      )}

      {line === 2 && (
        <Card
          sx={{
            margin: "16px 0",
            border: "1px solid",
            borderColor: theme.palette.primary.main,
          }}
        >
          <CardContent title="Type" sx={style}>
            <Typography component="h6" variant="h6">
              {t("settings.drawer.finishes.optionals")}
            </Typography>
            <Typography>
              {line === 1
                ? t("settings.drawer.finishes.convert_upgrade")
                : t("settings.drawer.finishes.convert_downgrade")}
            </Typography>
            <Button
              fullWidth
              size="large"
              variant="outlined"
              color="inherit"
              onClick={handleConvertMaterial}
              sx={{
                fontSize: 14,
                textTransform: "none",
              }}
            >
              {t("settings.drawer.finishes.convert")}
            </Button>
          </CardContent>
        </Card>
      )}
    </>
  );
}
